import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SubscriptionPlansModal from './SubscriptionPlansModal';
import { getCompanySubscription } from '../api/subscription';
import { toast } from 'react-toastify';

const SubscriptionGuard = ({ children }) => {
  const navigate = useNavigate();
  const authState = useSelector((store) => store.auth);
  const data = useSelector((store) => store.subAdmin.detail);
  const resolvedIsCompany = data?.is_company & authState?.user_type === "company"  ??   1;
  
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [companySubscription, setCompanySubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  // Check if company has active subscription
  const hasActiveSubscription = () => {
    if (!companySubscription) return false;
    
    // Handle different possible status values
    const status = companySubscription.status?.toString().toLowerCase();
    console.log('Checking subscription status:', status); // Debug log
    
    // Consider subscription active if status is one of these values
    const activeStatuses = ['active', '1', 1, 'paid', 'subscribed', 'enabled', 'trial', 'trialing'];
    return activeStatuses.includes(status);
  };

  // Fetch company subscription data
  const fetchCompanySubscription = async () => {
    setLoadingSubscription(true);
    try {
      const companyId = data?.id || authState?.company_id;
      if (companyId) {
        const response = await getCompanySubscription(companyId);
        console.log('Subscription API Response:', response); // Debug log
        
        if (response?.code === 1 && response?.data) {
          console.log('Subscription data:', response.data); // Debug log
          setCompanySubscription(response.data);
        } else {
          console.log('No subscription data found:', response); // Debug log
          setCompanySubscription(null);
        }
      }
    } catch (error) {
      console.error('Error fetching company subscription:', error);
      setCompanySubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  useEffect(() => {
    if (resolvedIsCompany) {
      fetchCompanySubscription();
    } else {
      setLoadingSubscription(false);
    }
  }, [resolvedIsCompany]);

  useEffect(() => {
    // Auto-show subscription modal for companies without active subscriptions
    if (resolvedIsCompany && !loadingSubscription && !hasActiveSubscription()) {
      setShowSubscriptionModal(true);
      
      // Show warning message
      toast.warning("Please purchase a subscription to access this feature.", {
        autoClose: 3000,
        toastId: 'subscription-warning'
      });
    }
  }, [resolvedIsCompany, loadingSubscription, companySubscription]);

  // If not a company user, allow access
  if (!resolvedIsCompany) {
    return children;
  }

  // If loading, show loading state
  if (loadingSubscription) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If company has active subscription, allow access
  if (hasActiveSubscription()) {
    return children;
  }

  // If company doesn't have active subscription, show subscription modal
  return (
    <>
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: 'rgba(0,0,0,0.05)' }}>
        <div className="text-center">
          <div className="mb-4">
            <i className="fa fa-crown fa-3x text-warning"></i>
          </div>
          <h3 className="mb-3">Subscription Required</h3>
          <p className="text-muted mb-4">Please purchase a subscription to access this feature.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowSubscriptionModal(true)}
          >
            <i className="fa fa-crown me-2"></i>
            View Subscription Plans
          </button>
        </div>
      </div>
      
      <SubscriptionPlansModal
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        companySubscription={companySubscription}
      />
    </>
  );
};

export default SubscriptionGuard;
